import { defineMessages } from '@/i18n'

/** Inventory: cable drums, labels, scanning, locations (docs/inventory-design.md, phase 2). */
export const inventoryMessages = defineMessages({
  en: {
    title: 'Inventory',
    subtitle: 'Cable drums: where they are, who has them, and how much is left.',
    loading: 'Loading inventory',
    loadingDrum: 'Loading drum',
    tileStock: 'In the warehouse',
    tileSite: 'At site stores',
    tileCrew: 'With crews',
    tileMetres: 'Metres on drums',
    scan: 'Scan',
    printLabels: 'Print labels',
    locations: 'Locations',
    filterAll: 'All',
    searchPlaceholder: 'Code, cable or crew…',
    statusUnregistered: 'Label not on a drum yet',
    statusInStock: 'In the warehouse',
    statusAtSite: 'At a site store',
    statusWithCrew: 'With a crew',
    statusWrittenOff: 'Written off',
    colCode: 'Code',
    colCable: 'Cable',
    colWhere: 'Where',
    colProject: 'Project',
    colLeft: 'Metres left',
    colLast: 'Last moved',
    noDrums: 'No drums match.',
    estimate: 'estimate',
    drumNotFound: "This isn't a known drum label. Check the code, or print labels from the Inventory page.",
    unregisteredTitle: 'New label — register the drum',
    unregisteredHint: "This label isn't on a drum yet. Say what's on the drum and where it was received.",
    unregisteredNoRight: "This label isn't on a drum yet. Someone with Inventory movements can register it.",
    cable: 'Cable',
    searchCable: 'Search cable or provider…',
    lengthDelivered: 'Length on delivery',
    innerMark: 'Meter mark at the inner end (optional)',
    innerMarkHint: 'Lets later readings of the outer-end mark give the exact metres left.',
    deliveryNote: 'Delivery note / lot',
    supplierRef: 'Delivery note / lot (optional)',
    receivedAt: 'Received at',
    note: 'Note (optional)',
    register: 'Register drum',
    delivered: 'Delivered',
    left: 'Left',
    where: 'Where',
    holder: 'Crew',
    project: 'Project',
    provider: 'Provider',
    actTransfer: 'Take to a site store',
    actIssue: 'Give to a crew',
    actReturn: 'Return',
    actCount: 'Stocktake',
    actWriteOff: 'Write off',
    history: 'History',
    writtenOffNote: "Written off — this drum can't be moved any more.",
    kindReceive: 'Received',
    kindTransfer: 'Taken to',
    kindIssue: 'Given to',
    kindReturn: 'Returned to',
    kindCount: 'Counted',
    kindWriteOff: 'Written off',
    by: 'by {name}',
    markReading: 'meter mark {mark} m',
    to: 'To',
    toSiteHint: 'Site stores first.',
    forProject: 'For project',
    crew: 'Crew',
    searchCrew: 'Search crew…',
    metresLeft: 'Metres left on the drum',
    byMark: 'Meter mark',
    byEstimate: 'Estimate',
    byMetres: 'Metres',
    markHint: 'Read the mark at the outer end of the cable.',
    noInnerMark: "The inner-end mark wasn't recorded for this drum — estimate or give the metres.",
    keepMetres: 'Unchanged ({metres} m) — the drum is sealed',
    full: 'Full',
    threeQuarters: '¾',
    half: '½',
    quarter: '¼',
    empty: 'Empty',
    reason: 'Why',
    reasonEmpty: 'Empty',
    reasonDamaged: 'Damaged',
    reasonLost: 'Lost',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    labelsTitle: 'Print drum labels',
    labelsHint: 'Each label is a new code. Stick it on a drum and scan it to register what is on the drum.',
    labelsCount: 'How many labels',
    labelsSheets: { one: '{count} A4 sheet of 24', other: '{count} A4 sheets of 24' },
    labelsCreate: 'Create and print',
    labelsPrint: 'Print',
    labelsBack: 'Back to inventory',
    labelsPrintHint: 'Print at 100% on A4 label sheets (3 × 8, 70 × 37 mm). Weatherproof film labels hold on a drum flange.',
    labelBlank: 'Cable · length',
    scanTitle: 'Scan a drum',
    scanHint: 'Point the camera at the QR on the drum.',
    scanNoCamera: "This browser can't scan here — use the phone's camera app on the QR, or type the code.",
    typeCode: 'Or type the code',
    open: 'Open',
    badCode: "That isn't a valid drum code — check the characters.",
    locationsTitle: 'Locations',
    locationsHint: "The central warehouse and the Bauleiters' temporary site stores.",
    addLocation: 'Add location',
    kindCentral: 'Central warehouse',
    kindSite: 'Site store',
    name: 'Name',
    bauleiter: 'Bauleiter',
    projects: 'Projects it serves',
    active: 'Active',
    inactive: 'Inactive',
    edit: 'Edit',
    drumsAt: { one: '{count} drum', other: '{count} drums' },
    couldNotSave: 'Could not save: {error}',
    reconTitle: 'Reconciliation',
    reconSubtitle: 'Where the cable went: what came off the drums against what the crews reported laying.',
    reconLoading: 'Loading reconciliation',
    reconExport: 'Export for accounting (CSV)',
    reconOpen: 'Reconciliation',
    reconTileUsed: 'Off the drums',
    reconTileLaid: 'Reported laid',
    reconTileUnaccounted: 'Unaccounted',
    reconTileFlagged: 'Cables to check',
    reconHow: 'Used = sent to the project − returned − written off − still on site. Unaccounted = used − reported laid since the first drum arrived. Within ±{tol} m or ±{pct}% of used counts as fine.',
    reconEmpty: "No drums have been sent to a project yet. Once drums move, each project's cable is reconciled here.",
    colSent: 'Sent',
    colReturned: 'Returned',
    colWrittenOff: 'Written off',
    colOnSite: 'On site',
    colUsed: 'Used',
    colLaid: 'Laid',
    colUnaccounted: 'Unaccounted',
    statusOk: 'Matches',
    statusMissing: 'Cable missing',
    statusOver: 'More laid than used',
    hintMissing: 'More came off the drums than was reported laid: waste, loss or unreported work.',
    hintOver: 'More was reported laid than came off tracked drums: cable from untracked drums, or over-reporting.',
    shortWarn: 'Site store short by {metres} m for what the project still has to lay',
    hasEstimates: 'includes estimates',
    since: 'since {date}',
    crewsTitle: 'Crews',
    crewsHint: 'What came off a drum while the crew held it, against what the crew reported laying.',
    drumsCount: { one: '{count} drum', other: '{count} drums' },
  },
  de: {
    title: 'Lager',
    subtitle: 'Kabeltrommeln: wo sie sind, wer sie hat und wie viel noch drauf ist.',
    loading: 'Lager wird geladen',
    loadingDrum: 'Trommel wird geladen',
    tileStock: 'Im Lager',
    tileSite: 'In Baustellenlagern',
    tileCrew: 'Bei Kolonnen',
    tileMetres: 'Meter auf Trommeln',
    scan: 'Scannen',
    printLabels: 'Etiketten drucken',
    locations: 'Lagerorte',
    filterAll: 'Alle',
    searchPlaceholder: 'Code, Kabel oder Kolonne…',
    statusUnregistered: 'Etikett noch nicht auf einer Trommel',
    statusInStock: 'Im Lager',
    statusAtSite: 'Im Baustellenlager',
    statusWithCrew: 'Bei einer Kolonne',
    statusWrittenOff: 'Ausgebucht',
    colCode: 'Code',
    colCable: 'Kabel',
    colWhere: 'Wo',
    colProject: 'Projekt',
    colLeft: 'Restmeter',
    colLast: 'Zuletzt bewegt',
    noDrums: 'Keine passenden Trommeln.',
    estimate: 'geschätzt',
    drumNotFound: 'Das ist kein bekanntes Trommeletikett. Code prüfen oder Etiketten auf der Lager-Seite drucken.',
    unregisteredTitle: 'Neues Etikett — Trommel erfassen',
    unregisteredHint: 'Dieses Etikett ist noch auf keiner Trommel. Angeben, was auf der Trommel ist und wo sie eingegangen ist.',
    unregisteredNoRight: 'Dieses Etikett ist noch auf keiner Trommel. Erfassen kann es jemand mit dem Recht Lagerbewegungen.',
    cable: 'Kabel',
    searchCable: 'Kabel oder Netzbetreiber suchen…',
    lengthDelivered: 'Länge bei Lieferung',
    innerMark: 'Metermarke am inneren Ende (optional)',
    innerMarkHint: 'Damit ergeben spätere Ablesungen am äußeren Ende die genauen Restmeter.',
    deliveryNote: 'Lieferschein / Charge',
    supplierRef: 'Lieferschein / Charge (optional)',
    receivedAt: 'Eingegangen in',
    note: 'Notiz (optional)',
    register: 'Trommel erfassen',
    delivered: 'Geliefert',
    left: 'Rest',
    where: 'Wo',
    holder: 'Kolonne',
    project: 'Projekt',
    provider: 'Netzbetreiber',
    actTransfer: 'Ins Baustellenlager bringen',
    actIssue: 'An Kolonne ausgeben',
    actReturn: 'Zurückgeben',
    actCount: 'Inventur',
    actWriteOff: 'Ausbuchen',
    history: 'Verlauf',
    writtenOffNote: 'Ausgebucht — diese Trommel kann nicht mehr bewegt werden.',
    kindReceive: 'Eingegangen',
    kindTransfer: 'Gebracht nach',
    kindIssue: 'Ausgegeben an',
    kindReturn: 'Zurück nach',
    kindCount: 'Gezählt',
    kindWriteOff: 'Ausgebucht',
    by: 'von {name}',
    markReading: 'Metermarke {mark} m',
    to: 'Nach',
    toSiteHint: 'Baustellenlager zuerst.',
    forProject: 'Für Projekt',
    crew: 'Kolonne',
    searchCrew: 'Kolonne suchen…',
    metresLeft: 'Restmeter auf der Trommel',
    byMark: 'Metermarke',
    byEstimate: 'Schätzung',
    byMetres: 'Meter',
    markHint: 'Die Marke am äußeren Kabelende ablesen.',
    noInnerMark: 'Die Marke am inneren Ende wurde für diese Trommel nicht erfasst — schätzen oder Meter angeben.',
    keepMetres: 'Unverändert ({metres} m) — Trommel ist verschlossen',
    full: 'Voll',
    threeQuarters: '¾',
    half: '½',
    quarter: '¼',
    empty: 'Leer',
    reason: 'Grund',
    reasonEmpty: 'Leer',
    reasonDamaged: 'Beschädigt',
    reasonLost: 'Verloren',
    save: 'Speichern',
    cancel: 'Abbrechen',
    close: 'Schließen',
    labelsTitle: 'Trommeletiketten drucken',
    labelsHint: 'Jedes Etikett ist ein neuer Code. Auf eine Trommel kleben und scannen, um zu erfassen, was darauf ist.',
    labelsCount: 'Wie viele Etiketten',
    labelsSheets: { one: '{count} A4-Bogen à 24', other: '{count} A4-Bögen à 24' },
    labelsCreate: 'Erstellen und drucken',
    labelsPrint: 'Drucken',
    labelsBack: 'Zurück zum Lager',
    labelsPrintHint: 'Mit 100 % auf A4-Etikettenbögen drucken (3 × 8, 70 × 37 mm). Wetterfeste Folienetiketten halten am Trommelflansch.',
    labelBlank: 'Kabel · Länge',
    scanTitle: 'Trommel scannen',
    scanHint: 'Die Kamera auf den QR-Code der Trommel richten.',
    scanNoCamera: 'Dieser Browser kann hier nicht scannen — die Kamera-App des Telefons auf den QR-Code richten oder den Code eintippen.',
    typeCode: 'Oder Code eintippen',
    open: 'Öffnen',
    badCode: 'Das ist kein gültiger Trommelcode — Zeichen prüfen.',
    locationsTitle: 'Lagerorte',
    locationsHint: 'Das Zentrallager und die temporären Baustellenlager der Bauleiter.',
    addLocation: 'Lagerort hinzufügen',
    kindCentral: 'Zentrallager',
    kindSite: 'Baustellenlager',
    name: 'Name',
    bauleiter: 'Bauleiter',
    projects: 'Versorgte Projekte',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    edit: 'Bearbeiten',
    drumsAt: { one: '{count} Trommel', other: '{count} Trommeln' },
    couldNotSave: 'Speichern fehlgeschlagen: {error}',
    reconTitle: 'Abgleich',
    reconSubtitle: 'Wohin das Kabel ging: was von den Trommeln kam, gegenüber dem, was die Kolonnen als verlegt gemeldet haben.',
    reconLoading: 'Abgleich wird geladen',
    reconExport: 'Export für die Buchhaltung (CSV)',
    reconOpen: 'Abgleich',
    reconTileUsed: 'Von den Trommeln',
    reconTileLaid: 'Als verlegt gemeldet',
    reconTileUnaccounted: 'Ungeklärt',
    reconTileFlagged: 'Zu prüfende Kabel',
    reconHow: 'Verbraucht = an das Projekt gesendet − zurück − ausgebucht − noch vor Ort. Ungeklärt = verbraucht − als verlegt gemeldet seit Eingang der ersten Trommel. Innerhalb ±{tol} m oder ±{pct} % des Verbrauchs gilt als in Ordnung.',
    reconEmpty: 'Noch keine Trommel wurde an ein Projekt gesendet. Sobald Trommeln bewegt werden, wird hier das Kabel jedes Projekts abgeglichen.',
    colSent: 'Gesendet',
    colReturned: 'Zurück',
    colWrittenOff: 'Ausgebucht',
    colOnSite: 'Vor Ort',
    colUsed: 'Verbraucht',
    colLaid: 'Verlegt',
    colUnaccounted: 'Ungeklärt',
    statusOk: 'Passt',
    statusMissing: 'Kabel fehlt',
    statusOver: 'Mehr verlegt als verbraucht',
    hintMissing: 'Es kam mehr von den Trommeln, als als verlegt gemeldet wurde: Verschnitt, Verlust oder nicht gemeldete Arbeit.',
    hintOver: 'Es wurde mehr als verlegt gemeldet, als von erfassten Trommeln kam: Kabel von nicht erfassten Trommeln oder zu viel gemeldet.',
    shortWarn: 'Baustellenlager hat {metres} m zu wenig für das, was das Projekt noch verlegen muss',
    hasEstimates: 'enthält Schätzungen',
    since: 'seit {date}',
    crewsTitle: 'Kolonnen',
    crewsHint: 'Was von einer Trommel kam, während die Kolonne sie hatte, gegenüber dem, was die Kolonne als verlegt gemeldet hat.',
    drumsCount: { one: '{count} Trommel', other: '{count} Trommeln' },
  },
  cnr: {
    title: 'Magacin',
    subtitle: 'Bubnjevi kabla: gdje su, ko ih ima i koliko je ostalo.',
    loading: 'Učitavanje magacina',
    loadingDrum: 'Učitavanje bubnja',
    tileStock: 'U magacinu',
    tileSite: 'U magacinima na gradilištu',
    tileCrew: 'Kod kolona',
    tileMetres: 'Metara na bubnjevima',
    scan: 'Skeniraj',
    printLabels: 'Štampaj etikete',
    locations: 'Lokacije',
    filterAll: 'Sve',
    searchPlaceholder: 'Šifra, kabl ili kolona…',
    statusUnregistered: 'Etiketa još nije na bubnju',
    statusInStock: 'U magacinu',
    statusAtSite: 'U magacinu na gradilištu',
    statusWithCrew: 'Kod kolone',
    statusWrittenOff: 'Otpisan',
    colCode: 'Šifra',
    colCable: 'Kabl',
    colWhere: 'Gdje',
    colProject: 'Projekat',
    colLeft: 'Preostalo metara',
    colLast: 'Posljednje pomjeranje',
    noDrums: 'Nema bubnjeva koji odgovaraju.',
    estimate: 'procjena',
    drumNotFound: 'Ovo nije poznata etiketa bubnja. Provjerite šifru ili štampajte etikete na stranici Magacin.',
    unregisteredTitle: 'Nova etiketa — upiši bubanj',
    unregisteredHint: 'Ova etiketa još nije na bubnju. Upišite šta je na bubnju i gdje je primljen.',
    unregisteredNoRight: 'Ova etiketa još nije na bubnju. Može je upisati neko sa pravom Kretanje magacina.',
    cable: 'Kabl',
    searchCable: 'Traži kabl ili provajdera…',
    lengthDelivered: 'Dužina pri isporuci',
    innerMark: 'Metarska oznaka na unutrašnjem kraju (neobavezno)',
    innerMarkHint: 'Tako kasnija očitavanja na spoljnom kraju daju tačne preostale metre.',
    deliveryNote: 'Otpremnica / lot',
    supplierRef: 'Otpremnica / lot (neobavezno)',
    receivedAt: 'Primljeno u',
    note: 'Napomena (neobavezno)',
    register: 'Upiši bubanj',
    delivered: 'Isporučeno',
    left: 'Ostalo',
    where: 'Gdje',
    holder: 'Kolona',
    project: 'Projekat',
    provider: 'Provajder',
    actTransfer: 'Odnesi u magacin na gradilištu',
    actIssue: 'Daj koloni',
    actReturn: 'Vrati',
    actCount: 'Popis',
    actWriteOff: 'Otpiši',
    history: 'Istorija',
    writtenOffNote: 'Otpisan — ovaj bubanj se više ne može pomjerati.',
    kindReceive: 'Primljen',
    kindTransfer: 'Odnesen u',
    kindIssue: 'Dat koloni',
    kindReturn: 'Vraćen u',
    kindCount: 'Popisan',
    kindWriteOff: 'Otpisan',
    by: '— {name}',
    markReading: 'metarska oznaka {mark} m',
    to: 'U',
    toSiteHint: 'Prvo magacini na gradilištu.',
    forProject: 'Za projekat',
    crew: 'Kolona',
    searchCrew: 'Traži kolonu…',
    metresLeft: 'Preostali metri na bubnju',
    byMark: 'Metarska oznaka',
    byEstimate: 'Procjena',
    byMetres: 'Metri',
    markHint: 'Očitajte oznaku na spoljnom kraju kabla.',
    noInnerMark: 'Oznaka na unutrašnjem kraju nije upisana za ovaj bubanj — procijenite ili upišite metre.',
    keepMetres: 'Bez promjene ({metres} m) — bubanj je zatvoren',
    full: 'Pun',
    threeQuarters: '¾',
    half: '½',
    quarter: '¼',
    empty: 'Prazan',
    reason: 'Razlog',
    reasonEmpty: 'Prazan',
    reasonDamaged: 'Oštećen',
    reasonLost: 'Izgubljen',
    save: 'Sačuvaj',
    cancel: 'Otkaži',
    close: 'Zatvori',
    labelsTitle: 'Štampaj etikete za bubnjeve',
    labelsHint: 'Svaka etiketa je nova šifra. Zalijepite je na bubanj i skenirajte da upišete šta je na njemu.',
    labelsCount: 'Koliko etiketa',
    labelsSheets: { one: '{count} A4 list sa 24', few: '{count} A4 lista sa 24', other: '{count} A4 listova sa 24' },
    labelsCreate: 'Napravi i štampaj',
    labelsPrint: 'Štampaj',
    labelsBack: 'Nazad na magacin',
    labelsPrintHint: 'Štampajte na 100% na A4 listove etiketa (3 × 8, 70 × 37 mm). Vodootporne folijske etikete drže na obodu bubnja.',
    labelBlank: 'Kabl · dužina',
    scanTitle: 'Skeniraj bubanj',
    scanHint: 'Usmjerite kameru na QR kod na bubnju.',
    scanNoCamera: 'Ovaj pregledač ovdje ne može skenirati — koristite kameru telefona na QR kodu ili upišite šifru.',
    typeCode: 'Ili upišite šifru',
    open: 'Otvori',
    badCode: 'To nije ispravna šifra bubnja — provjerite znakove.',
    locationsTitle: 'Lokacije',
    locationsHint: 'Centralni magacin i privremeni magacini Bauleitera na gradilištu.',
    addLocation: 'Dodaj lokaciju',
    kindCentral: 'Centralni magacin',
    kindSite: 'Magacin na gradilištu',
    name: 'Naziv',
    bauleiter: 'Bauleiter',
    projects: 'Projekti koje snabdijeva',
    active: 'Aktivna',
    inactive: 'Neaktivna',
    edit: 'Izmijeni',
    drumsAt: { one: '{count} bubanj', few: '{count} bubnja', other: '{count} bubnjeva' },
    couldNotSave: 'Čuvanje nije uspjelo: {error}',
    reconTitle: 'Usklađivanje',
    reconSubtitle: 'Gdje je otišao kabl: šta je skinuto sa bubnjeva u odnosu na ono što su kolone prijavile kao položeno.',
    reconLoading: 'Učitavanje usklađivanja',
    reconExport: 'Izvoz za računovodstvo (CSV)',
    reconOpen: 'Usklađivanje',
    reconTileUsed: 'Skinuto sa bubnjeva',
    reconTileLaid: 'Prijavljeno položeno',
    reconTileUnaccounted: 'Neobjašnjeno',
    reconTileFlagged: 'Kablovi za provjeru',
    reconHow: 'Potrošeno = poslato na projekat − vraćeno − otpisano − još na gradilištu. Neobjašnjeno = potrošeno − prijavljeno položeno od dolaska prvog bubnja. Unutar ±{tol} m ili ±{pct}% potrošenog smatra se u redu.',
    reconEmpty: 'Nijedan bubanj još nije poslat na projekat. Kad se bubnjevi pomjeraju, kabl svakog projekta se ovdje usklađuje.',
    colSent: 'Poslato',
    colReturned: 'Vraćeno',
    colWrittenOff: 'Otpisano',
    colOnSite: 'Na gradilištu',
    colUsed: 'Potrošeno',
    colLaid: 'Položeno',
    colUnaccounted: 'Neobjašnjeno',
    statusOk: 'Odgovara',
    statusMissing: 'Nedostaje kabla',
    statusOver: 'Položeno više nego potrošeno',
    hintMissing: 'Sa bubnjeva je skinuto više nego što je prijavljeno kao položeno: otpad, gubitak ili neprijavljen rad.',
    hintOver: 'Prijavljeno je više položenog nego što je skinuto sa praćenih bubnjeva: kabl sa nepraćenih bubnjeva ili previše prijavljeno.',
    shortWarn: 'Magacinu na gradilištu nedostaje {metres} m za ono što projekat još treba da položi',
    hasEstimates: 'uključuje procjene',
    since: 'od {date}',
    crewsTitle: 'Kolone',
    crewsHint: 'Šta je skinuto sa bubnja dok ga je kolona imala, u odnosu na ono što je kolona prijavila kao položeno.',
    drumsCount: { one: '{count} bubanj', few: '{count} bubnja', other: '{count} bubnjeva' },
  },
  bs: {
    title: 'Magacin',
    subtitle: 'Bubnjevi kabla: gdje su, ko ih ima i koliko je ostalo.',
    loading: 'Učitavanje magacina',
    loadingDrum: 'Učitavanje bubnja',
    tileStock: 'U magacinu',
    tileSite: 'U magacinima na gradilištu',
    tileCrew: 'Kod kolona',
    tileMetres: 'Metara na bubnjevima',
    scan: 'Skeniraj',
    printLabels: 'Štampaj etikete',
    locations: 'Lokacije',
    filterAll: 'Sve',
    searchPlaceholder: 'Šifra, kabl ili kolona…',
    statusUnregistered: 'Etiketa još nije na bubnju',
    statusInStock: 'U magacinu',
    statusAtSite: 'U magacinu na gradilištu',
    statusWithCrew: 'Kod kolone',
    statusWrittenOff: 'Otpisan',
    colCode: 'Šifra',
    colCable: 'Kabl',
    colWhere: 'Gdje',
    colProject: 'Projekat',
    colLeft: 'Preostalo metara',
    colLast: 'Posljednje pomjeranje',
    noDrums: 'Nema bubnjeva koji odgovaraju.',
    estimate: 'procjena',
    drumNotFound: 'Ovo nije poznata etiketa bubnja. Provjerite šifru ili štampajte etikete na stranici Magacin.',
    unregisteredTitle: 'Nova etiketa — upiši bubanj',
    unregisteredHint: 'Ova etiketa još nije na bubnju. Upišite šta je na bubnju i gdje je primljen.',
    unregisteredNoRight: 'Ova etiketa još nije na bubnju. Može je upisati neko sa pravom Kretanje magacina.',
    cable: 'Kabl',
    searchCable: 'Traži kabl ili provajdera…',
    lengthDelivered: 'Dužina pri isporuci',
    innerMark: 'Metarska oznaka na unutrašnjem kraju (neobavezno)',
    innerMarkHint: 'Tako kasnija očitavanja na spoljnom kraju daju tačne preostale metre.',
    deliveryNote: 'Otpremnica / lot',
    supplierRef: 'Otpremnica / lot (neobavezno)',
    receivedAt: 'Primljeno u',
    note: 'Napomena (neobavezno)',
    register: 'Upiši bubanj',
    delivered: 'Isporučeno',
    left: 'Ostalo',
    where: 'Gdje',
    holder: 'Kolona',
    project: 'Projekat',
    provider: 'Provajder',
    actTransfer: 'Odnesi u magacin na gradilištu',
    actIssue: 'Daj koloni',
    actReturn: 'Vrati',
    actCount: 'Popis',
    actWriteOff: 'Otpiši',
    history: 'Historija',
    writtenOffNote: 'Otpisan — ovaj bubanj se više ne može pomjerati.',
    kindReceive: 'Primljen',
    kindTransfer: 'Odnesen u',
    kindIssue: 'Dat koloni',
    kindReturn: 'Vraćen u',
    kindCount: 'Popisan',
    kindWriteOff: 'Otpisan',
    by: '— {name}',
    markReading: 'metarska oznaka {mark} m',
    to: 'U',
    toSiteHint: 'Prvo magacini na gradilištu.',
    forProject: 'Za projekat',
    crew: 'Kolona',
    searchCrew: 'Traži kolonu…',
    metresLeft: 'Preostali metri na bubnju',
    byMark: 'Metarska oznaka',
    byEstimate: 'Procjena',
    byMetres: 'Metri',
    markHint: 'Očitajte oznaku na spoljnom kraju kabla.',
    noInnerMark: 'Oznaka na unutrašnjem kraju nije upisana za ovaj bubanj — procijenite ili upišite metre.',
    keepMetres: 'Bez promjene ({metres} m) — bubanj je zatvoren',
    full: 'Pun',
    threeQuarters: '¾',
    half: '½',
    quarter: '¼',
    empty: 'Prazan',
    reason: 'Razlog',
    reasonEmpty: 'Prazan',
    reasonDamaged: 'Oštećen',
    reasonLost: 'Izgubljen',
    save: 'Spasi',
    cancel: 'Otkaži',
    close: 'Zatvori',
    labelsTitle: 'Štampaj etikete za bubnjeve',
    labelsHint: 'Svaka etiketa je nova šifra. Zalijepite je na bubanj i skenirajte da upišete šta je na njemu.',
    labelsCount: 'Koliko etiketa',
    labelsSheets: { one: '{count} A4 list sa 24', few: '{count} A4 lista sa 24', other: '{count} A4 listova sa 24' },
    labelsCreate: 'Napravi i štampaj',
    labelsPrint: 'Štampaj',
    labelsBack: 'Nazad na magacin',
    labelsPrintHint: 'Štampajte na 100% na A4 listove etiketa (3 × 8, 70 × 37 mm). Vodootporne folijske etikete drže na obodu bubnja.',
    labelBlank: 'Kabl · dužina',
    scanTitle: 'Skeniraj bubanj',
    scanHint: 'Usmjerite kameru na QR kod na bubnju.',
    scanNoCamera: 'Ovaj pregledač ovdje ne može skenirati — koristite kameru telefona na QR kodu ili upišite šifru.',
    typeCode: 'Ili upišite šifru',
    open: 'Otvori',
    badCode: 'To nije ispravna šifra bubnja — provjerite znakove.',
    locationsTitle: 'Lokacije',
    locationsHint: 'Centralni magacin i privremeni magacini Bauleitera na gradilištu.',
    addLocation: 'Dodaj lokaciju',
    kindCentral: 'Centralni magacin',
    kindSite: 'Magacin na gradilištu',
    name: 'Naziv',
    bauleiter: 'Bauleiter',
    projects: 'Projekti koje snabdijeva',
    active: 'Aktivna',
    inactive: 'Neaktivna',
    edit: 'Uredi',
    drumsAt: { one: '{count} bubanj', few: '{count} bubnja', other: '{count} bubnjeva' },
    couldNotSave: 'Čuvanje nije uspjelo: {error}',
    reconTitle: 'Usklađivanje',
    reconSubtitle: 'Gdje je otišao kabl: šta je skinuto sa bubnjeva u odnosu na ono što su kolone prijavile kao položeno.',
    reconLoading: 'Učitavanje usklađivanja',
    reconExport: 'Izvoz za računovodstvo (CSV)',
    reconOpen: 'Usklađivanje',
    reconTileUsed: 'Skinuto sa bubnjeva',
    reconTileLaid: 'Prijavljeno položeno',
    reconTileUnaccounted: 'Neobjašnjeno',
    reconTileFlagged: 'Kablovi za provjeru',
    reconHow: 'Potrošeno = poslato na projekat − vraćeno − otpisano − još na gradilištu. Neobjašnjeno = potrošeno − prijavljeno položeno od dolaska prvog bubnja. Unutar ±{tol} m ili ±{pct}% potrošenog smatra se u redu.',
    reconEmpty: 'Nijedan bubanj još nije poslat na projekat. Kad se bubnjevi pomjeraju, kabl svakog projekta se ovdje usklađuje.',
    colSent: 'Poslato',
    colReturned: 'Vraćeno',
    colWrittenOff: 'Otpisano',
    colOnSite: 'Na gradilištu',
    colUsed: 'Potrošeno',
    colLaid: 'Položeno',
    colUnaccounted: 'Neobjašnjeno',
    statusOk: 'Odgovara',
    statusMissing: 'Nedostaje kabla',
    statusOver: 'Položeno više nego potrošeno',
    hintMissing: 'Sa bubnjeva je skinuto više nego što je prijavljeno kao položeno: otpad, gubitak ili neprijavljen rad.',
    hintOver: 'Prijavljeno je više položenog nego što je skinuto sa praćenih bubnjeva: kabl sa nepraćenih bubnjeva ili previše prijavljeno.',
    shortWarn: 'Magacinu na gradilištu nedostaje {metres} m za ono što projekat još treba položiti',
    hasEstimates: 'uključuje procjene',
    since: 'od {date}',
    crewsTitle: 'Kolone',
    crewsHint: 'Šta je skinuto sa bubnja dok ga je kolona imala, u odnosu na ono što je kolona prijavila kao položeno.',
    drumsCount: { one: '{count} bubanj', few: '{count} bubnja', other: '{count} bubnjeva' },
  },
  sr: {
    title: 'Magacin',
    subtitle: 'Bubnjevi kabla: gde su, ko ih ima i koliko je ostalo.',
    loading: 'Učitavanje magacina',
    loadingDrum: 'Učitavanje bubnja',
    tileStock: 'U magacinu',
    tileSite: 'U magacinima na gradilištu',
    tileCrew: 'Kod kolona',
    tileMetres: 'Metara na bubnjevima',
    scan: 'Skeniraj',
    printLabels: 'Štampaj etikete',
    locations: 'Lokacije',
    filterAll: 'Sve',
    searchPlaceholder: 'Šifra, kabl ili kolona…',
    statusUnregistered: 'Etiketa još nije na bubnju',
    statusInStock: 'U magacinu',
    statusAtSite: 'U magacinu na gradilištu',
    statusWithCrew: 'Kod kolone',
    statusWrittenOff: 'Otpisan',
    colCode: 'Šifra',
    colCable: 'Kabl',
    colWhere: 'Gde',
    colProject: 'Projekat',
    colLeft: 'Preostalo metara',
    colLast: 'Poslednje pomeranje',
    noDrums: 'Nema bubnjeva koji odgovaraju.',
    estimate: 'procena',
    drumNotFound: 'Ovo nije poznata etiketa bubnja. Proverite šifru ili štampajte etikete na stranici Magacin.',
    unregisteredTitle: 'Nova etiketa — upiši bubanj',
    unregisteredHint: 'Ova etiketa još nije na bubnju. Upišite šta je na bubnju i gde je primljen.',
    unregisteredNoRight: 'Ova etiketa još nije na bubnju. Može je upisati neko sa pravom Kretanje magacina.',
    cable: 'Kabl',
    searchCable: 'Traži kabl ili provajdera…',
    lengthDelivered: 'Dužina pri isporuci',
    innerMark: 'Metarska oznaka na unutrašnjem kraju (neobavezno)',
    innerMarkHint: 'Tako kasnija očitavanja na spoljnom kraju daju tačne preostale metre.',
    deliveryNote: 'Otpremnica / lot',
    supplierRef: 'Otpremnica / lot (neobavezno)',
    receivedAt: 'Primljeno u',
    note: 'Napomena (neobavezno)',
    register: 'Upiši bubanj',
    delivered: 'Isporučeno',
    left: 'Ostalo',
    where: 'Gde',
    holder: 'Kolona',
    project: 'Projekat',
    provider: 'Provajder',
    actTransfer: 'Odnesi u magacin na gradilištu',
    actIssue: 'Daj koloni',
    actReturn: 'Vrati',
    actCount: 'Popis',
    actWriteOff: 'Otpiši',
    history: 'Istorija',
    writtenOffNote: 'Otpisan — ovaj bubanj se više ne može pomerati.',
    kindReceive: 'Primljen',
    kindTransfer: 'Odnesen u',
    kindIssue: 'Dat koloni',
    kindReturn: 'Vraćen u',
    kindCount: 'Popisan',
    kindWriteOff: 'Otpisan',
    by: '— {name}',
    markReading: 'metarska oznaka {mark} m',
    to: 'U',
    toSiteHint: 'Prvo magacini na gradilištu.',
    forProject: 'Za projekat',
    crew: 'Kolona',
    searchCrew: 'Traži kolonu…',
    metresLeft: 'Preostali metri na bubnju',
    byMark: 'Metarska oznaka',
    byEstimate: 'Procena',
    byMetres: 'Metri',
    markHint: 'Očitajte oznaku na spoljnom kraju kabla.',
    noInnerMark: 'Oznaka na unutrašnjem kraju nije upisana za ovaj bubanj — procenite ili upišite metre.',
    keepMetres: 'Bez promene ({metres} m) — bubanj je zatvoren',
    full: 'Pun',
    threeQuarters: '¾',
    half: '½',
    quarter: '¼',
    empty: 'Prazan',
    reason: 'Razlog',
    reasonEmpty: 'Prazan',
    reasonDamaged: 'Oštećen',
    reasonLost: 'Izgubljen',
    save: 'Sačuvaj',
    cancel: 'Otkaži',
    close: 'Zatvori',
    labelsTitle: 'Štampaj etikete za bubnjeve',
    labelsHint: 'Svaka etiketa je nova šifra. Zalepite je na bubanj i skenirajte da upišete šta je na njemu.',
    labelsCount: 'Koliko etiketa',
    labelsSheets: { one: '{count} A4 list sa 24', few: '{count} A4 lista sa 24', other: '{count} A4 listova sa 24' },
    labelsCreate: 'Napravi i štampaj',
    labelsPrint: 'Štampaj',
    labelsBack: 'Nazad na magacin',
    labelsPrintHint: 'Štampajte na 100% na A4 listove etiketa (3 × 8, 70 × 37 mm). Vodootporne folijske etikete drže na obodu bubnja.',
    labelBlank: 'Kabl · dužina',
    scanTitle: 'Skeniraj bubanj',
    scanHint: 'Usmerite kameru na QR kod na bubnju.',
    scanNoCamera: 'Ovaj pregledač ovdje ne može skenirati — koristite kameru telefona na QR kodu ili upišite šifru.',
    typeCode: 'Ili upišite šifru',
    open: 'Otvori',
    badCode: 'To nije ispravna šifra bubnja — proverite znakove.',
    locationsTitle: 'Lokacije',
    locationsHint: 'Centralni magacin i privremeni magacini Bauleitera na gradilištu.',
    addLocation: 'Dodaj lokaciju',
    kindCentral: 'Centralni magacin',
    kindSite: 'Magacin na gradilištu',
    name: 'Naziv',
    bauleiter: 'Bauleiter',
    projects: 'Projekti koje snabdeva',
    active: 'Aktivna',
    inactive: 'Neaktivna',
    edit: 'Izmeni',
    drumsAt: { one: '{count} bubanj', few: '{count} bubnja', other: '{count} bubnjeva' },
    couldNotSave: 'Čuvanje nije uspelo: {error}',
    reconTitle: 'Usklađivanje',
    reconSubtitle: 'Gde je otišao kabl: šta je skinuto sa bubnjeva u odnosu na ono što su kolone prijavile kao položeno.',
    reconLoading: 'Učitavanje usklađivanja',
    reconExport: 'Izvoz za računovodstvo (CSV)',
    reconOpen: 'Usklađivanje',
    reconTileUsed: 'Skinuto sa bubnjeva',
    reconTileLaid: 'Prijavljeno položeno',
    reconTileUnaccounted: 'Neobjašnjeno',
    reconTileFlagged: 'Kablovi za proveru',
    reconHow: 'Potrošeno = poslato na projekat − vraćeno − otpisano − još na gradilištu. Neobjašnjeno = potrošeno − prijavljeno položeno od dolaska prvog bubnja. Unutar ±{tol} m ili ±{pct}% potrošenog smatra se u redu.',
    reconEmpty: 'Nijedan bubanj još nije poslat na projekat. Kad se bubnjevi pomeraju, kabl svakog projekta se ovde usklađuje.',
    colSent: 'Poslato',
    colReturned: 'Vraćeno',
    colWrittenOff: 'Otpisano',
    colOnSite: 'Na gradilištu',
    colUsed: 'Potrošeno',
    colLaid: 'Položeno',
    colUnaccounted: 'Neobjašnjeno',
    statusOk: 'Odgovara',
    statusMissing: 'Nedostaje kabla',
    statusOver: 'Položeno više nego potrošeno',
    hintMissing: 'Sa bubnjeva je skinuto više nego što je prijavljeno kao položeno: otpad, gubitak ili neprijavljen rad.',
    hintOver: 'Prijavljeno je više položenog nego što je skinuto sa praćenih bubnjeva: kabl sa nepraćenih bubnjeva ili previše prijavljeno.',
    shortWarn: 'Magacinu na gradilištu nedostaje {metres} m za ono što projekat još treba da položi',
    hasEstimates: 'uključuje procene',
    since: 'od {date}',
    crewsTitle: 'Kolone',
    crewsHint: 'Šta je skinuto sa bubnja dok ga je kolona imala, u odnosu na ono što je kolona prijavila kao položeno.',
    drumsCount: { one: '{count} bubanj', few: '{count} bubnja', other: '{count} bubnjeva' },
  },
})
